import { Color, Icon, IconName, RightElementVariant, Typography, TypographyVariant, UseState } from '@elearning-platform/ui';
import React, { useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { CalculationType } from '../../enums';

import { CalculationStatus } from './calculation.enums';
import { Container, InputsContainer, MessageContainer, StyledInput } from './calculation.styled';
import { CalculationProps, MessageProperties } from './calculation.types';

export const Calculation: React.FC<CalculationProps> = (props: CalculationProps): JSX.Element => {
  const {
    firstFieldData,
    secondFieldData,
    resultFieldData,
    calculationType,
    answer,
    correctionText,
    isDisabled,
    onSolvedStateChange,
    correctAnswerText,
  }: CalculationProps = props;
  const { t }: TransProps<never> = useTranslation();
  const [firstFieldValue, setFirstFieldValue]: UseState<string> = useState<string>('');
  const [secondFieldValue, setSecondFieldValue]: UseState<string> = useState<string>('');
  const [attemptsCount, setAttemptsCount]: UseState<number> = useState<number>(0);
  const [message, setMessage]: UseState<string | undefined> = useState<string | undefined>();
  const [isResetEnabled, setIsResetEnabled]: UseState<boolean> = useState<boolean>(false);
  const [calculationStatus, setCalculationStatus]: UseState<CalculationStatus | undefined> = useState<CalculationStatus | undefined>();

  const operationSymbol: string = useMemo((): string => {
    switch (calculationType) {
      case CalculationType.Addition:
        return '+';
      case CalculationType.Substraction:
        return '-';
      case CalculationType.Multiplication:
      default:
        return 'x';
    }
  }, [calculationType]);

  const resultValue: number | undefined = useMemo((): number | undefined => {
    if (firstFieldValue && secondFieldValue) {
      const firstActualValue: number = firstFieldData.isPercentValue ? Number(firstFieldValue) / 100 : Number(firstFieldValue);
      const secondActualValue: number = secondFieldData.isPercentValue ? Number(secondFieldValue) / 100 : Number(secondFieldValue);
      switch (calculationType) {
        case CalculationType.Addition:
          return firstActualValue + secondActualValue;
        case CalculationType.Substraction:
          return firstActualValue - secondActualValue;
        case CalculationType.Multiplication:
          return firstActualValue * secondActualValue;
      }
    }
  }, [firstFieldValue, secondFieldValue, calculationType, firstFieldData, secondFieldData]);

  const messageProperties: MessageProperties | undefined = useMemo((): MessageProperties | undefined => {
    if (calculationStatus === CalculationStatus.LastFailure) {
      return {
        backgroundColor: Color.White,
      };
    }
    if (calculationStatus === CalculationStatus.Failure) {
      return {
        iconName: IconName.ClearCircle,
        iconColor: Color.Red900,
        backgroundColor: Color.Red100,
      };
    }
    if (calculationStatus === CalculationStatus.Success) {
      return {
        iconName: IconName.CheckmarkCircle,
        iconColor: Color.Green900,
        backgroundColor: Color.Green100,
      };
    }
  }, [calculationStatus]);

  const resetCalculation: () => void = (): void => {
    setFirstFieldValue('');
    setSecondFieldValue('');
    setMessage(undefined);
    setIsResetEnabled(false);
  };

  const checkAnswer: () => void = (): void => {
    if (resultValue === answer) {
      setCalculationStatus(CalculationStatus.Success);
      setMessage(correctAnswerText);
      setAttemptsCount(0);
      if (onSolvedStateChange) {
        onSolvedStateChange(true);
      }
    } else if (attemptsCount === 2) {
      setCalculationStatus(CalculationStatus.LastFailure);
      setMessage(correctionText);
      setAttemptsCount(0);
      setIsResetEnabled(true);
      if (onSolvedStateChange) {
        onSolvedStateChange(false);
      }
    } else {
      setCalculationStatus(CalculationStatus.Failure);
      setMessage(t('competence.app.scrollLesson.componentsV2.calculation.wrongAnswerText'));
      setAttemptsCount(attemptsCount + 1);
      setIsResetEnabled(true);
      if (onSolvedStateChange) {
        onSolvedStateChange(false);
      }
    }
  };

  const handleBlur: () => void = (): void => {
    if (firstFieldValue && secondFieldValue) {
      checkAnswer();
    }
  };

  const handleFocus: () => void = (): void => {
    if (isResetEnabled) {
      resetCalculation();
    }
  };

  return (
    <Container>
      <InputsContainer>
        <StyledInput
          disabled={isDisabled}
          isRightElementInlineOnFocus
          label={t(firstFieldData.label)}
          onBlur={handleBlur}
          onChange={(e: React.FormEvent<HTMLInputElement>): void => setFirstFieldValue(e.currentTarget.value)}
          onFocus={handleFocus}
          rightElement={firstFieldData.unit}
          rightElementVariant={RightElementVariant.Centered}
          rightElementWidth={firstFieldData.unit ? 40 : undefined}
          type='number'
          value={firstFieldValue}
        />
        <Typography variant={TypographyVariant.TextMD}>{operationSymbol}</Typography>
        <StyledInput
          disabled={isDisabled}
          isRightElementInlineOnFocus
          label={t(secondFieldData.label)}
          onBlur={handleBlur}
          onChange={(e: React.FormEvent<HTMLInputElement>): void => setSecondFieldValue(e.currentTarget.value)}
          onFocus={handleFocus}
          rightElement={secondFieldData.unit}
          rightElementVariant={RightElementVariant.Centered}
          rightElementWidth={secondFieldData.unit ? 40 : undefined}
          type='number'
          value={secondFieldValue}
        />
        <Typography variant={TypographyVariant.TextMD}>=</Typography>
        <StyledInput
          disabled
          label={t(resultFieldData.label)}
          rightElement={resultFieldData.unit}
          rightElementVariant={RightElementVariant.Inline}
          rightElementWidth={resultFieldData.unit ? 40 : undefined}
          value={resultValue?.toString() ?? '0'}
        />
      </InputsContainer>
      {!!message && !!messageProperties && (
        <MessageContainer $bgColor={messageProperties.backgroundColor}>
          <Typography variant={TypographyVariant.TextSM}>{message}</Typography>
          {!!messageProperties.iconName && <Icon color={messageProperties.iconColor} name={messageProperties.iconName} size={20} />}
        </MessageContainer>
      )}
    </Container>
  );
};
